import { Ionicons } from '@expo/vector-icons';
import { router, useLocalSearchParams, useNavigation } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Linking, Platform, Share, StyleSheet, TouchableOpacity, View } from 'react-native';
import { RadioButton } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { WebView } from 'react-native-webview';

import { useCreateNpoPaymentAccountSteps, useGetTermsCreateNpo } from '@/api/hooks/paymentAccountsHooks';
import { NonProfitOrg } from '@/api/models/nonProfitOrgModels';
import { GetTermsResponse } from '@/api/models/paymentAccountsModels';
import BrandedButton from '@/components/buttons/BrandedButton';
import DotsLoader from '@/components/DotsLoader';
import Text from '@/components/Text';
import { SIGN_IN_BUTTON_BOTTOM } from '@/constants/layout';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';

const NpoPaymentOnboarding = () => {
  const { npoId, npoItem } = useLocalSearchParams<{
    npoId: string;
    npoItem: string;
  }>();
  const navigation = useNavigation();

  const { mutateAsync: getTerms, isPending: getTermsPending } = useGetTermsCreateNpo();
  const { mutateAsync: saveStep, isPending: createStepPending } = useCreateNpoPaymentAccountSteps();

  const [npoData, setNpoData] = useState<NonProfitOrg | undefined>(undefined);
  const [nextButtonDisabled, setNextButtonDisabled] = useState(true);
  const [termsResponse, setTermsResponse] = useState<GetTermsResponse | undefined>();

  useLayoutEffect(() => {
    const handleShare = async () =>
      Share.share({
        ...(Platform.OS === 'ios'
          ? { url: termsResponse?.termsAndConditionsURL ?? '' }
          : { message: termsResponse?.termsAndConditionsURL ?? '' }),
        title: i18n.t('mix.terms_of_services')
      }).catch((e) => {
        console.error(e);
      });

    navigation.setOptions({
      headerRight: () =>
        termsResponse?.termsAndConditionsURL && (
          <TouchableOpacity onPress={handleShare}>
            <Ionicons size={24} name="share-outline" color={'black'} />
          </TouchableOpacity>
        )
    });
  }, [navigation, termsResponse]);

  useEffect(() => {
    if (npoItem) {
      const parsedNpo = safeJsonParse<NonProfitOrg>(npoItem);
      if (parsedNpo) setNpoData(parsedNpo);
    }
  }, [npoItem]);

  useEffect(() => {
    if (npoData) {
      getTerms(
        { nonProfitOrgId: npoData.id ?? '', type: 'card' },
        {
          onSuccess: (data) => {
            setTermsResponse(data);
          },
          onError: (e) => {
            console.error(e);
          }
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npoData]);

  const openTermsWeb = async () => {
    if (termsResponse?.termsAndConditionsURL) await WebBrowser.openBrowserAsync(termsResponse?.termsAndConditionsURL);
  };

  const onAccept = () => {
    if (npoData) {
      saveStep(
        {
          type: 'both',
          body: {
            nonProfitOrgId: npoId,
            acceptTos: true
          }
        },
        {
          onSuccess: (data) => {
            router.replace({
              pathname: '/npoPaymentOnboarding/general',
              params: {
                npoId,
                npoItem
              }
            });
          },
          onError: (e) => {
            console.error(e);
          }
        }
      );
    }
  };

  return (
    <SafeAreaView style={style.container} edges={['bottom']}>
      {getTermsPending ? (
        <View style={style.loader}>
          <DotsLoader />
        </View>
      ) : (
        <View style={style.flexOne}>
          {Platform.OS === 'web' ? (
            <BrandedButton style={style.viewWeb} onPress={openTermsWeb}>
              {i18n.t('npoPaymentOnboarding.terms_cc')}
            </BrandedButton>
          ) : (
            <WebView
              bounces={false}
              showsVerticalScrollIndicator={false}
              containerStyle={style.webViewContainer}
              startInLoadingState
              renderLoading={() => (
                <View style={style.webLoader}>
                  <DotsLoader />
                </View>
              )}
              onShouldStartLoadWithRequest={(request) => {
                if (request.url !== termsResponse?.termsAndConditionsURL) {
                  Linking.openURL(request.url);
                  return false;
                } else return true;
              }}
              contentMode="mobile"
              source={{
                uri: termsResponse?.termsAndConditionsURL ?? ''
              }}
            />
          )}
        </View>
      )}

      <View style={style.acceptTermsContainer}>
        <RadioButton.Android
          disabled={termsResponse?.termsAndConditionsURL === undefined}
          value={'accept-terms'}
          status={!nextButtonDisabled ? 'checked' : 'unchecked'}
          color="black"
          onPress={() => setNextButtonDisabled(!nextButtonDisabled)}
        />
        <Text fontWeight="700" style={style.acceptTermsLabel}>
          {i18n.t('npoPaymentOnboarding.terms_readed')}
        </Text>
      </View>

      <BrandedButton style={style.button} onPress={onAccept} loading={createStepPending} disabled={nextButtonDisabled}>
        {i18n.t('npoPaymentOnboarding.accept_continue')}
      </BrandedButton>
    </SafeAreaView>
  );
};

export default NpoPaymentOnboarding;

const style = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  container: {
    flex: 1,
    paddingHorizontal: 18,
    gap: 10
  },

  loader: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },

  webLoader: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center'
  },

  webViewContainer: {
    paddingBottom: 10
  },

  acceptTermsContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center'
  },

  acceptTermsLabel: {
    flex: 1,
    fontSize: 14,
    color: 'black'
  },

  viewWeb: {
    marginTop: 55,
    alignSelf: 'center',
    width: '100%'
  },

  button: {
    marginBottom: Platform.OS === 'web' ? SIGN_IN_BUTTON_BOTTOM : 0
  }
});
